import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { PleasewaitPopup } from "../../../components/PleasewaitPopup";
import { baseURL } from "../../../config/api";
import CustomFormatDatePicker from "../../../components/CustomFormatDatePicker";
import { PhoneNumberInput } from "../../../components/PhoneNumberInput";

export const EditDriver = () => {
    const navigate = useNavigate();
    const idNumber = useLocation();

    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [phone, setPhone] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [licenseNumber, setLicenseNumber] = useState<string>("");
    const [licenseExpiryDate, setLicenseExpiryDate] = useState<string>("");
    const [status, setStatus] = useState<string>("available");
    const [enabled, setEnabled] = useState<string>("Yes");
    const [isEmailValid, setIsEmailValid] = useState(true);
    const [pleaseWaitPopup, setPleaseWaitPopup] =
        useState<boolean>(false);

    const [driverData, setDriverData] = useState<any>(null);

    useEffect(() => {
        let timer: NodeJS.Timeout;

        if (email.trim() !== "") {
            timer = setTimeout(() => {
                const valid =
                    /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(email.trim());

                setIsEmailValid(valid);
            }, 800);
        } else {
            setIsEmailValid(true);
        }

        return () => clearTimeout(timer);
    }, [email]);

    useEffect(() => {
        setPleaseWaitPopup(true);

        axios
            .get(`${baseURL}drivers/${idNumber?.state}`)
            .then((response) => {
                const driver = response?.data?.data;

                setDriverData(driver);

                setFirstName(driver?.first_name ?? "");
                setLastName(driver?.last_name ?? "");
                setPhone(driver?.phone ?? "");
                setEmail(driver?.email ?? "");
                setLicenseNumber(driver?.license_number ?? "");

                setLicenseExpiryDate(
                    driver?.license_expiry_date
                        ? driver.license_expiry_date.split("T")[0]
                        : ""
                );

                setStatus(driver?.status ?? "available");

                setEnabled(
                    driver?.is_active
                        ? "Yes"
                        : "No"
                );

                setPleaseWaitPopup(false);
            })
            .catch((error) => {
                setPleaseWaitPopup(false);

                console.error(
                    "Error fetching driver:",
                    error
                );
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idNumber]);

    const handleSubmit = async (
        e: React.FormEvent<HTMLFormElement>
    ) => {
        e.preventDefault();

        setPleaseWaitPopup(true);

        const payload = {
            ...driverData,

            first_name: firstName.trim(),
            last_name: lastName.trim(),
            phone: phone.trim(),
            email: email.trim(),
            license_number: licenseNumber.trim(),
            license_expiry_date: licenseExpiryDate,
            status,
            is_active: enabled === "Yes"
        };

        await axios
            .put(
                `${baseURL}drivers/${idNumber?.state}`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json"
                    },
                    withCredentials: false
                }
            )
            .then((response) => {
                setPleaseWaitPopup(false);
                navigate(-1);
            })
            .catch((error) => {
                setPleaseWaitPopup(false);

                console.error(
                    "Error updating driver:",
                    error
                );
            });
    };

    return (
        <>
            <div className="px-[24px] py-[20px] h-[calc(100vh-142px)] overflow-auto">

                <div className="w-full bg-white p-4 rounded-md">

                    <h2 className="text-lg font-semibold">
                        Edit Driver
                    </h2>

                    <div className="mt-2 p-2">

                        <form onSubmit={handleSubmit}>

                            <div className="w-full">

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-2.5">

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            First Name{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>

                                        <input
                                            type="text"
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={firstName}
                                            placeholder="First Name"
                                            onChange={(e) =>
                                                setFirstName(e.target.value)
                                            }
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Last Name{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>

                                        <input
                                            type="text"
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={lastName}
                                            placeholder="Last Name"
                                            onChange={(e) =>
                                                setLastName(e.target.value)
                                            }
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Email{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>

                                        <input
                                            type="email"
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={email}
                                            placeholder="Email Address"
                                            onChange={(e) =>
                                                setEmail(e.target.value)
                                            }
                                            required
                                        />
                                        {!isEmailValid && (
                                            <p className="text-xs text-red-500 mt-1">
                                                Please enter a valid email address
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Phone{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>

                                        <PhoneNumberInput
                                            name={"phoneNumber"}
                                            value={phone}
                                            setValue={setPhone}
                                            fontSize={"14px"}
                                            required={true}
                                        />
                                    </div>

                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-2.5">
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            License Number{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>

                                        <input
                                            type="text"
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={licenseNumber}
                                            placeholder="License Number"
                                            onChange={(e) =>
                                                setLicenseNumber(
                                                    e.target.value
                                                )
                                            }
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            License Expiry Date{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>

                                        <CustomFormatDatePicker
                                            value={licenseExpiryDate}
                                            onChange={(value) => {
                                                setLicenseExpiryDate(value);
                                            }}
                                            required={true}
                                        />
                                    </div>

                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-2.5">

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Status{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>

                                        <select
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={status}
                                            onChange={(e) =>
                                                setStatus(e.target.value)
                                            }
                                            required
                                        >
                                            <option value="available">
                                                Available
                                            </option>

                                            <option value="unavailable">
                                                Unavailable
                                            </option>

                                            <option value="on_trip">
                                                On Trip
                                            </option>

                                            <option value="inactive">
                                                Inactive
                                            </option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Active?
                                        </label>

                                        <select
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={enabled}
                                            onChange={(e) =>
                                                setEnabled(e.target.value)
                                            }
                                        >
                                            <option value="Yes">
                                                Yes
                                            </option>

                                            <option value="No">
                                                No
                                            </option>
                                        </select>
                                    </div>

                                </div>

                                <div className="flex flex-col sm:flex-row gap-4 mt-6">

                                    <button
                                        type="submit"
                                        className="w-full sm:w-auto px-4 py-2 rounded bg-primary text-white cursor-pointer"
                                    >
                                        Save
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => navigate(-1)}
                                        className="w-full sm:w-auto px-4 py-2 rounded bg-white text-secondary cursor-pointer border border-primary"
                                    >
                                        Cancel
                                    </button>

                                </div>

                            </div>

                        </form>

                    </div>
                </div>
            </div>

            <PleasewaitPopup
                showPopup={pleaseWaitPopup}
            />
        </>
    );
};